/**
 * client/element-skins-renderer.ts
 * Responsabilidad: Renderizado modular y optimizado de las Skins Elementales (criaturas base)
 * tanto para WebGL (PixiJS v8) como para Canvas 2D fallback.
 */

import { Sprite, Graphics } from "pixi.js";
import { getElementSkinTexture, getElementSkinImage } from "./element-skins";

/**
 * Dibuja la skin elemental de la criatura en PixiJS v8:
 * - Aplica respiración suave viva.
 * - Dimensiona el Sprite proporcionalmente al radio de masa.
 * - Proyecta un aura perimetral viva y sutil armónica con el elemento.
 */
export function drawPixiElementSkin(
    sprite: Sprite,
    auraGraphics: Graphics,
    skinId: string,
    radius: number,
    element: string,
    nowMs: number,
    playerZoom: number
): boolean {
    if (!Number.isFinite(radius) || radius <= 0) {
        sprite.visible = false;
        auraGraphics.clear();
        return false;
    }

    // 1. Obtener textura PixiJS validada
    const tex = getElementSkinTexture(skinId);
    if (!tex) {
        sprite.visible = false;
        sprite.scale.set(1, 1);
        return false;
    }
    if (sprite.texture !== tex) {
        sprite.texture = tex;
    }

    const breath = 1.0 + 0.02 * Math.sin(nowMs * 0.004);
    const diameter = radius * 2 * 1.08 * breath;

    sprite.width = diameter;
    sprite.height = diameter;
    sprite.anchor.set(0.5, 0.5);
    sprite.visible = true;

    // 2. Aura perimetral sutil armónica con el elemento
    auraGraphics.clear();
    const el = (element || "water").toLowerCase();
    const auraR = radius * 1.02 * breath;

    switch (el) {
        case "electric":
        case "electrico":
        case "electricidad": {
            // Chispas voltaicas y destellos sutiles
            const sparkCount = 6;
            for (let i = 0; i < sparkCount; i++) {
                const ang = (nowMs * 0.003) + (i * (Math.PI * 2 / sparkCount));
                const dist = auraR + (Math.sin(nowMs * 0.008 + i) * 3 + 4) * playerZoom;
                const px = Math.cos(ang) * dist;
                const py = Math.sin(ang) * dist;
                const size = Math.max(1.2, (2.2 + Math.sin(nowMs * 0.01 + i) * 0.8) * playerZoom);
                auraGraphics.circle(px, py, size).fill({ color: 0xfacc15, alpha: 0.75 });
            }
            break;
        }

        case "fire":
        case "fuego": {
            // Resplandor cálido y chispas de brasa
            const emberCount = 5;
            for (let i = 0; i < emberCount; i++) {
                const ang = (nowMs * 0.002) + (i * (Math.PI * 2 / emberCount));
                const dist = auraR + (Math.sin(nowMs * 0.006 + i) * 3 + 3) * playerZoom;
                const px = Math.cos(ang) * dist;
                const py = Math.sin(ang) * dist;
                const size = Math.max(1.2, (2.0 + Math.sin(nowMs * 0.009 + i) * 0.6) * playerZoom);
                auraGraphics.circle(px, py, size).fill({ color: 0xf97316, alpha: 0.7 });
            }
            break;
        }

        case "water":
        case "agua": {
            // Gotas y resplandor acuático suave
            const dropCount = 5;
            for (let i = 0; i < dropCount; i++) {
                const ang = (nowMs * 0.0018) + (i * (Math.PI * 2 / dropCount));
                const dist = auraR + (Math.sin(nowMs * 0.005 + i) * 2.5 + 3) * playerZoom;
                const px = Math.cos(ang) * dist;
                const py = Math.sin(ang) * dist;
                const size = Math.max(1.2, 2.2 * playerZoom);
                auraGraphics.circle(px, py, size).fill({ color: 0x38bdf8, alpha: 0.65 });
            }
            break;
        }

        case "plant":
        case "planta": {
            // Esporas y hojitas esmeralda flotantes
            const leafCount = 5;
            for (let i = 0; i < leafCount; i++) {
                const ang = (nowMs * 0.0015) + (i * (Math.PI * 2 / leafCount));
                const dist = auraR + (Math.sin(nowMs * 0.004 + i) * 2.8 + 3) * playerZoom;
                const px = Math.cos(ang) * dist;
                const py = Math.sin(ang) * dist;
                const size = Math.max(1.2, 2.0 * playerZoom);
                auraGraphics.circle(px, py, size).fill({ color: 0x4ade80, alpha: 0.7 });
            }
            break;
        }

        case "earth":
        case "tierra":
        default: {
            // Gravilla y fragmentos facetados de ámbar
            const rockCount = 5;
            for (let i = 0; i < rockCount; i++) {
                const ang = (nowMs * 0.0016) + (i * (Math.PI * 2 / rockCount));
                const dist = auraR + (Math.sin(nowMs * 0.004 + i) * 2.5 + 3) * playerZoom;
                const px = Math.cos(ang) * dist;
                const py = Math.sin(ang) * dist;
                const size = Math.max(1.2, 2.4 * playerZoom);
                auraGraphics.circle(px, py, size).fill({ color: 0xd97706, alpha: 0.68 });
            }
            break;
        }
    }
    return true;
}

/**
 * Dibuja la skin elemental en Canvas 2D fallback:
 * - Respiración suave coordinada.
 * - Dibujado centrado en (x, y) con dimensiones proporcionales al radio.
 * - Retorna true si la imagen fue renderizada exitosamente.
 */
export function drawCanvasElementSkin(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    skinId: string,
    element: string,
    nowMs: number,
    _zoom: number = 1.0
): boolean {
    if (!Number.isFinite(radius) || radius <= 0) return false;

    const img = getElementSkinImage(skinId);
    if (!img || !img.complete || img.naturalWidth === 0) {
        return false;
    }

    const breath = 1.0 + 0.02 * Math.sin(nowMs * 0.004);
    const diameter = radius * 2 * 1.08 * breath;
    const half = diameter / 2;

    ctx.save();

    // Sutil aura elemental perimetral en Canvas 2D
    const el = (element || "water").toLowerCase();
    let glowColor = "rgba(56, 189, 248, 0.35)";
    if (el === "fire" || el === "fuego") glowColor = "rgba(249, 115, 22, 0.35)";
    else if (el === "plant" || el === "planta") glowColor = "rgba(74, 222, 128, 0.35)";
    else if (el === "electric" || el === "electrico" || el === "electricidad") glowColor = "rgba(250, 204, 21, 0.35)";
    else if (el === "earth" || el === "tierra") glowColor = "rgba(217, 119, 6, 0.35)";

    const grad = ctx.createRadialGradient(x, y, radius * 0.6, x, y, radius * 1.15 * breath);
    grad.addColorStop(0, "rgba(0, 0, 0, 0)");
    grad.addColorStop(1, glowColor);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, radius * 1.15 * breath, 0, Math.PI * 2);
    ctx.fill();

    // Dibujado del avatar de la criatura elemental
    ctx.drawImage(img, x - half, y - half, diameter, diameter);
    ctx.restore();
    return true;
}

